'use client';

import { useRef, createElement, Fragment, useEffect } from 'react';
import { autocomplete } from '@algolia/autocomplete-js';
import { createRoot } from 'react-dom/client';
import { useParams } from 'next/navigation';
import '@algolia/autocomplete-theme-classic';
import SearchResult from '@src/components/Shared/SearchResult/SearchResult';
import { HitItem, Items, SearchModalPropTypes, AlgoliaHit } from './SearchModal.type';
import './SearchModal.css';

// Import types from autocomplete
import type { AutocompleteSource, GetSources } from '@algolia/autocomplete-js';

// Debounce helper to delay execution of API calls
const debouncePromise = <T extends (...args: any[]) => Promise<any>,>(fn: T, time: number) => {
  let timerId: ReturnType<typeof setTimeout>;
  return function debounced(...args: Parameters<T>): Promise<ReturnType<T>> {
    if (timerId) {
      clearTimeout(timerId);
    }
    return new Promise(resolve => {
      timerId = setTimeout(() => resolve(fn(...args)), time);
    });
  };
};

// Function to send events to Google Analytics
const sendSearchEventToGA = (action: string, params: any): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, params);
  }
};

// Debounced fetch declared once outside the component
const debouncedFetchQuery = debouncePromise(async (query: string, locale: string) => {
  const res = await fetch(`/api/search?query=${encodeURIComponent(query)}&locale=${locale}`);
  return res.json();
}, 300);
const SearchModal = ({
  onHandleModelToggle,
  globalTextBlock
}: SearchModalPropTypes) => {
  const {
    locale
  } = useParams<{
    locale: string;
  }>();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const rootRef = useRef<HTMLElement | undefined>();
  const debounced = debouncePromise((items: any) => Promise.resolve(items), 300);
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const search = autocomplete<AlgoliaHit>({
      container: containerRef.current,
      renderer: {
        createElement,
        Fragment,
        render: () => {}
      },
      render({
        children
      }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;
          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }
        panelRootRef.current.render(children);
      },
      detachedMediaQuery: 'none',
      insights: true,
      // Explicitly type getSources to match GetSources<AlgoliaHit>
      getSources: (async ({
        query
      }: {
        query: string;
      }): Promise<AutocompleteSource<AlgoliaHit>[]> => {
        const debouncedFetch = await debouncedFetchQuery(query, locale);
        return [{
          sourceId: 'general pages',
          getItems(): Promise<AlgoliaHit[]> {
            // Log search query event for Google Analytics.
            sendSearchEventToGA('search query', {
              query
            });
            return Promise.resolve(debouncedFetch.generalPages);
          },
          templates: {
            // @ts-expect-error: This line intentionally bypasses type checking
            header({
              items
            }: Items) {
              if (items.length === 0) return null;
              return <>
                    <span className='aa-SourceHeaderTitle'>{globalTextBlock?.items?.[0]?.pages}</span>
                    <div className='aa-SourceHeaderLine' />
                  </>;
            },
            // @ts-expect-error: This line intentionally bypasses type checking
            item({
              item
            }: HitItem) {
              return <SearchResult locale={locale} item={item} globalTextBlock={globalTextBlock} />;
            }
          }
        }, {
          sourceId: 'blog',
          getItems(): Promise<AlgoliaHit[]> {
            return Promise.resolve(debouncedFetch.blog);
          },
          templates: {
            // @ts-expect-error: This line intentionally bypasses type checking
            header({
              items
            }: Items) {
              if (items.length === 0) return null;
              return <>
                    <span className='aa-SourceHeaderTitle'>{globalTextBlock?.items?.[0]?.blogArticles}</span>
                    <div className='aa-SourceHeaderLine' />
                  </>;
            },
            // @ts-expect-error: This line intentionally bypasses type checking
            item({
              item
            }: HitItem) {
              return <SearchResult locale={locale} item={item} globalTextBlock={globalTextBlock} />;
            }
          }
        }, {
          sourceId: 'all pages',
          getItems(): Promise<AlgoliaHit[]> {
            return Promise.resolve(debouncedFetch.allPages);
          },
          templates: {
            // @ts-expect-error: This line intentionally bypasses type checking
            item() {
              return null;
            },
            noResults() {
              // Log no result event for Google Analytics.
              sendSearchEventToGA('No result search query', {
                query
              });
              return <p>
                    {globalTextBlock?.items?.[0]?.noResultsFor} <span className='font-[500]'>{`"${query}"`}</span>
                  </p>;
            }
          }
        }];
      }) as GetSources<AlgoliaHit> // Casting the function as the expected type.
    });

    // Focus the search input when the modal opens.
    const focusSearchInput = (): void => {
      const input = containerRef.current?.querySelector('input');
      if (input) {
        input.focus();
      }
    };
    focusSearchInput();
    return () => {
      search.destroy();
    };
  }, [debounced, locale, globalTextBlock, onHandleModelToggle]);
  return <div className='search-modal-fade fixed left-[0] top-[0] z-[101] flex h-[100%] w-[100%] justify-center' data-sentry-component="SearchModal" data-sentry-source-file="SearchModal.tsx">
      <div onClick={onHandleModelToggle} className='fixed left-[0] top-[0] h-[100%] w-[100%] bg-[#00000050]'></div>
      <div className='z-[106] mx-[2rem] mt-[15rem] h-fit w-full shadow-lg md:mx-[0] md:w-[70rem] 3xl:w-[80rem]'>
        <div ref={containerRef}></div>
      </div>
    </div>;
};
export default SearchModal;